import React, { useState } from 'react';
import { ClipboardCopy, Trash2, Pencil, PlusCircle } from 'lucide-react';
import CreateIntentModel from './CreateIntentModel';

const intentModels = [
    {
        id: 'intent001',
        name: 'Invoice Intents',
        description: 'Handles user requests for invoice creation and management.',
        workflows: ['Generate Invoice', 'Email Invoice'],
        rulesCount: 5,
        createdBy: 'Admin',
        updated: '2 days ago'
    },
    {
        id: 'intent002',
        name: 'Leave Management',
        description: 'Processes leave applications and approvals.',
        workflows: ['Apply Leave', 'Approve Leave'],
        rulesCount: 3,
        createdBy: 'HR Manager',
        updated: '1 week ago'
    },
    {
        id: 'intent003',
        name: 'Report Requests',
        description: 'Responds to requests for generating different reports.',
        workflows: ['Weekly Report', 'Monthly Summary'],
        rulesCount: 4,
        createdBy: 'Ops Lead',
        updated: '5 days ago'
    },
    {
        id: 'intent004',
        name: 'Patient Scheduling',
        description: 'Handles appointment scheduling and rescheduling in healthcare systems.',
        workflows: ['Book Appointment', 'Reschedule Appointment'],
        rulesCount: 5,
        createdBy: 'Health Admin',
        updated: '3 days ago'
    },
    {
        id: 'intent005',
        name: 'Order Tracking',
        description: 'Manages customer queries related to order status and shipment.',
        workflows: ['Track Order', 'Update Order Status'],
        rulesCount: 6,
        createdBy: 'Logistics Team',
        updated: '2 days ago'
    },
    {
        id: 'intent006',
        name: 'Loan Applications',
        description: 'Processes user queries and workflows related to loan requests and status.',
        workflows: ['Apply Loan', 'Check Loan Status'],
        rulesCount: 5,
        createdBy: 'Finance Bot',
        updated: '1 week ago'
    },
    {
        id: 'intent007',
        name: 'Student Support',
        description: 'Automates student queries on admissions, schedules, and grades.',
        workflows: ['Admission Info', 'Exam Schedule', 'Result Inquiry'],
        rulesCount: 7,
        createdBy: 'Academic Services',
        updated: '4 days ago'
    },
    {
        id: 'intent008',
        name: 'HR Onboarding',
        description: 'Covers onboarding flows for new employees including document collection.',
        workflows: ['Submit Documents', 'Assign Buddy', 'Initiate Training'],
        rulesCount: 6,
        createdBy: 'HR Lead',
        updated: '2 days ago'
    },
    {
        id: 'intent009',
        name: 'IT Support',
        description: 'Manages IT service requests and incident resolution through chat.',
        workflows: ['Raise Ticket', 'Reset Password', 'Device Setup'],
        rulesCount: 9,
        createdBy: 'IT Service Desk',
        updated: 'Today'
    },
    {
        id: 'intent010',
        name: 'Restaurant Reservations',
        description: 'Handles table bookings and reservation status updates.',
        workflows: ['Book Table', 'Modify Reservation', 'Cancel Booking'],
        rulesCount: 4,
        createdBy: 'Hospitality Ops',
        updated: 'Yesterday'
    },
    {
        id: 'intent011',
        name: 'Travel Assistance',
        description: 'Manages user intent around travel bookings, reschedules, and updates.',
        workflows: ['Book Flight', 'Cancel Trip', 'Modify Booking'],
        rulesCount: 6,
        createdBy: 'Travel Agent AI',
        updated: '1 week ago'
    },
    {
        id: 'intent012',
        name: 'Retail Promotions',
        description: 'Handles inquiries about discounts, deals, and coupon validations.',
        workflows: ['Check Offers', 'Validate Coupon'],
        rulesCount: 3,
        createdBy: 'Retail Marketing',
        updated: '5 days ago'
    },
    {
        id: 'intent013',
        name: 'Banking Queries',
        description: 'Answers banking-related customer requests and account info access.',
        workflows: ['Check Balance', 'Transaction History', 'Report Fraud'],
        rulesCount: 8,
        createdBy: 'Banking AI Desk',
        updated: 'Today'
    },
    {
        id: 'intent014',
        name: 'Insurance Claims',
        description: 'Assists users in filing, tracking, and understanding insurance claims.',
        workflows: ['File Claim', 'Track Claim Status', 'Update Policy'],
        rulesCount: 7,
        createdBy: 'Insurance Ops',
        updated: '3 days ago'
    },
    {
        id: 'intent015',
        name: 'Manufacturing Work Orders',
        description: 'Handles work order requests and status updates for factory operations.',
        workflows: ['Create Work Order', 'Check Work Order Status'],
        rulesCount: 4,
        createdBy: 'Factory Supervisor',
        updated: '4 days ago'
    },
    {
        id: 'intent016',
        name: 'Procurement Requests',
        description: 'Automates vendor quote requests and order placements.',
        workflows: ['Request Quote', 'Place Order', 'Approve Purchase'],
        rulesCount: 5,
        createdBy: 'Procurement Admin',
        updated: '6 days ago'
    },
    {
        id: 'intent017',
        name: 'Legal Document Automation',
        description: 'Manages legal document generation and case status tracking.',
        workflows: ['Generate NDA', 'Track Case', 'Request Legal Advice'],
        rulesCount: 6,
        createdBy: 'Legal AI',
        updated: '1 day ago'
    },
    {
        id: 'intent018',
        name: 'Energy Consumption Reports',
        description: 'Allows users to check and forecast energy usage.',
        workflows: ['Daily Report', 'Usage Forecast', 'Alert Threshold'],
        rulesCount: 4,
        createdBy: 'Energy AI',
        updated: '2 days ago'
    },
    {
        id: 'intent019',
        name: 'E-commerce Returns',
        description: 'Helps customers initiate and track return and refund processes.',
        workflows: ['Start Return', 'Check Refund Status'],
        rulesCount: 3,
        createdBy: 'Returns Bot',
        updated: '5 days ago'
    },
    {
        id: 'intent020',
        name: 'Healthcare Referrals',
        description: 'Supports doctors in referring patients and checking specialist availability.',
        workflows: ['Refer Patient', 'Check Specialist Schedule'],
        rulesCount: 5,
        createdBy: 'Care Coordinator',
        updated: '3 days ago'
    },
    {
        id: 'intent021',
        name: 'Real Estate Inquiries',
        description: 'Responds to queries related to property listings, pricing, and visits.',
        workflows: ['Schedule Visit', 'Send Property Info'],
        rulesCount: 4,
        createdBy: 'Real Estate Agent AI',
        updated: '6 days ago'
    },
    {
        id: 'intent022',
        name: 'Subscription Management',
        description: 'Handles subscription changes, upgrades, or cancellations.',
        workflows: ['Cancel Subscription', 'Upgrade Plan', 'Pause Service'],
        rulesCount: 4,
        createdBy: 'Customer Success',
        updated: '2 days ago'
    },
    {
        id: 'intent023',
        name: 'Event Registrations',
        description: 'Manages registrations, ticketing, and attendee confirmations.',
        workflows: ['Register User', 'Confirm Attendance', 'Send Reminder'],
        rulesCount: 5,
        createdBy: 'Event Coordinator',
        updated: 'Today'
    }
];

const IntentModelListing = () => {
    const [search, setSearch] = useState('');
    const [showCreateModal, setShowCreateModal] = useState(false);

    const filteredIntents = intentModels.filter((intent) =>
        intent.name.toLowerCase().includes(search.toLowerCase())
    );

    return (
        <div>
            {/* Header + Button */}
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h2 className="text-xl font-semibold text-white">Intent Orchestration</h2>
                    <p className="text-sm text-gray-400">Manage intent groups and map them to workflows and rules.</p>
                </div>
                <button onClick={() => setShowCreateModal(true)} className="flex items-center bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-md text-sm shadow">
                    <PlusCircle className="w-4 h-4 mr-2" /> New Intent Model
                </button>
            </div>

            {/* Search */}
            <div className="mb-4">
                <input
                    type="text"
                    placeholder="Search intent models..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full px-4 py-2 rounded-md bg-neutral-800 text-white placeholder-gray-400 focus:outline-none text-sm"
                />
            </div>

            {/* Table */}
            <div className="overflow-x-auto border border-neutral-800 rounded">
                <table className="min-w-full text-sm text-left text-gray-300">
                    <thead className="bg-neutral-900 text-gray-400 uppercase">
                        <tr>
                            <th className="px-4 py-2">Model Name</th>
                            <th className="px-4 py-2">Description</th>
                            <th className="px-4 py-2">Linked Workflows</th>
                            <th className="px-4 py-2">Rules</th>
                            <th className="px-4 py-2">Created By</th>
                            <th className="px-4 py-2">Last Updated</th>
                            <th className="px-4 py-2 text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredIntents.map((intent) => (
                            <tr key={intent.id} className="border-t border-neutral-800 hover:bg-neutral-800/30">
                                <td className="px-4 py-3 font-medium text-white">{intent.name}</td>
                                <td className="px-4 py-3">{intent.description}</td>
                                <td className="px-4 py-3 text-cyan-400">{intent.workflows.join(', ')}</td>
                                <td className="px-4 py-3">{intent.rulesCount}</td>
                                <td className="px-4 py-3">{intent.createdBy}</td>
                                <td className="px-4 py-3">{intent.updated}</td>
                                <td className="px-4 py-3 flex justify-end gap-2">
                                    <button className="text-yellow-400 hover:text-yellow-300" title="Edit">
                                        <Pencil className="w-4 h-4" />
                                    </button>
                                    <button className="text-red-400 hover:text-red-300" title="Delete">
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </td>
                            </tr>
                        ))}
                        {filteredIntents.length === 0 && (
                            <tr>
                                <td colSpan={7} className="text-center py-6 text-gray-500">
                                    No intent models found.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
            {showCreateModal && (
                <CreateIntentModel onClose={() => setShowCreateModal(false)} />
            )}
        </div>
    );
};

export default IntentModelListing;
